import { ProductEmailPreferences } from "./-components/product-email-preferences";
import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useAuth } from "@/hooks/auth/auth";
import { toast } from "sonner";
import { useAuthGetCurrentUser, useAuthUpdateAccount } from "@/orval/auth";
import { ProfileEmailVerification } from "./-components/profile-email-verification";
import { ProfilePhoneVerification } from "./-components/profile-phone-verification";
import { PhoneFormField } from "@/components/form/form-field/phone/phone-form-field";
import type { UpdateAccountDTO, UserDTO } from "@/orval/model";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { KeyRound, Save, TriangleAlert } from "lucide-react";
import { buttonVariants } from "@/components/ui/variants/button-variants";
import { useLayoutEffect } from "react";
import { CheckboxFormField } from "@/components/form/form-field/checkbox-form-field";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FormItem } from "@/components/form/hooks/form-item/useFormItem";
import { FormControl } from "@/components/form/form-control";
import { Checkbox } from "@/components/ui/checkbox";
import { FormLabel } from "@/components/form/form-label";
import { FormMessage } from "@/components/form/form-message";
import { validateOptionalSmsPhoneNumber } from "@/lib/phone-number";

const initialValues: UserDTO = {
  userName: "",
  email: "",
  id: 0,
  emailConfirmed: false,
  phoneNumber: "",
  phoneNumberConfirmed: false,
  twoFactorEnabled: false,
  firstName: "",
  lastName: "",
  pendingInvitationCount: 0,
  roles: [],
};

const MyProfilePage: React.FC = () => {
  const { data: user, isFetching } = useAuthGetCurrentUser();
  const { mutateAsync: updateUser } = useAuthUpdateAccount({ mutation: { meta: { invalidates: "none" } } });
  const { setToken, token } = useAuth();
  const queryClient = useQueryClient();

  const canEnable2FA = !!user?.emailConfirmed || !!user?.phoneNumberConfirmed;
  const mustShowIndeterminate = !!user?.twoFactorEnabled && !canEnable2FA;

  const onSubmit = async (data: UpdateAccountDTO) => {
    const { token, didSendEmail, didSendSms } = await updateUser({ data });

    if (token) {
      setToken(token);
    }

    let successMessage = "Your account has been updated successfully!";

    if (didSendEmail) {
      successMessage += "\nA confirmation email has been sent to your updated email address.";
    }

    if (didSendSms) {
      successMessage += "\nA confirmation SMS has been sent to your updated phone number.";
    }

    toast.success(successMessage);
  };

  useLayoutEffect(() => {
    if (token) {
      queryClient.invalidateQueries();
    }
  }, [token, queryClient]);

  return (
    <div className="flex items-center justify-center h-full flex-1">
      <Card className="max-w-full w-[512px] m-2">
        <CardHeader>
          <CardTitle>Your Profile</CardTitle>
          <CardDescription>Update your personal information below</CardDescription>
        </CardHeader>
        <CardContent>
          <Form<UpdateAccountDTO>
            onSubmit={onSubmit}
            useFormProps={{ defaultValues: user || initialValues }}
            isFetching={isFetching}
          >
            <FormField
              label="First Name"
              name="firstName"
              inputProps={{ placeholder: "Your first name", autoFocus: true }}
            />

            <FormField label="Last Name" name="lastName" inputProps={{ placeholder: "Your last name" }} />

            <div className="flex flex-col gap-2">
              <FormField
                label="Email"
                name="email"
                inputProps={{ placeholder: "Your email address" }}
                rules={{
                  required: true,
                  validate: (value) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(value) || "Please enter a valid email address";
                  },
                }}
              />

              {/* Email Verification Status UI */}
              <ProfileEmailVerification email={user?.email} emailConfirmed={user?.emailConfirmed} />
            </div>

            <div className="flex flex-col gap-2">
              <PhoneFormField
                label="Phone Number"
                name="phoneNumber"
                maskedInputProps={{ placeholder: "Your phone number" }}
                rules={{
                  required: false,
                  validate: validateOptionalSmsPhoneNumber,
                }}
              />

              <ProfilePhoneVerification phone={user?.phoneNumber} phoneConfirmed={user?.phoneNumberConfirmed} />
            </div>

            {/* 2FA Toggle */}
            <div className="mt-4">
              <CheckboxFormField
                name="twoFactorEnabled"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-x-2">
                      <FormControl>
                        <Checkbox
                          checked={mustShowIndeterminate ? "indeterminate" : field.value}
                          onCheckedChange={field.onChange}
                          disabled={!canEnable2FA}
                          id="2fa-toggle"
                        />
                      </FormControl>
                      <FormLabel className="font-normal">
                        <label htmlFor="2fa-toggle" className="flex items-center gap-1 cursor-pointer">
                          Enable Two-Factor Authentication (2FA)
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="ml-1 text-muted-foreground cursor-pointer">
                                  <Info size={16} />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                Add an extra layer of security to your account. When enabled, you'll be required to
                                provide a verification code sent to your email or phone number when logging in.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </label>
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {mustShowIndeterminate && (
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-xs text-yellow-800 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300 rounded pl-2 pr-3 py-1 font-semibold w-fit flex items-center gap-2 max-w-md">
                    <TriangleAlert className="inline size-4 text-yellow-600 flex-none" strokeWidth={2} />
                    Two-Factor Authentication is enabled, but you must verify your email or phone number for it to be
                    active.
                  </span>
                </div>
              )}
            </div>

            <IconButton type="submit" icon={<Save />} className="w-full">
              Save Changes
            </IconButton>
          </Form>
          <Link to="/my/change-password" className={buttonVariants({ variant: "outline", className: "mt-4 w-full" })}>
            <KeyRound className="mr-2 size-4" />
            Change Password
          </Link>
          <ProductEmailPreferences />
        </CardContent>
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/_app/my/profile")({
  component: MyProfilePage,
});
